<template>
  <div class="dropdown-select">
    <FakeSelect
      v-if="!noAutoSize"
      use-max-width
      :options="options"
      @max-width-change="labelWidth = `${$event + 36}px`"
    />
    <input
      type="hidden"
      :name="name"
      :value="selectedOption ? selectedOption.value : null"
    />
    <DropdownTarget ref="dropdownTarget" :full-width="fullWidth">
      <template #default="{ show, hide, visible }">
        <DropdownLabel
          :width="fullWidth || noAutoSize ? 'auto' : labelWidth"
          :label="selectedOption && selectedOption.title"
          :placeholder="placeholder"
          :disabled="disabled"
          @click="show"
          @keydown.native.up="onUp"
          @keydown.native.down="onDown"
          @keyup.native.enter="onEnter"
        />
        <BasicDropdown
          v-if="visible && !disabled"
          full-width
          placement="bottom"
          @close="hide"
        >
          <SuggestionItems
            :items="options"
            :active-suggestion="activeSuggestion"
            appearance="compact"
            @click="selectOption"
          />
        </BasicDropdown>
      </template>
    </DropdownTarget>
  </div>
</template>

<script>
import { resolveCircularIndex, rotateIndex } from 'src/utils/math.js';
import { createPropModel } from 'src/utils/vue.js';

import BasicDropdown from 'src/components/BasicDropdown/BasicDropdown.vue';
import DropdownLabel from 'src/components/DropdownLabel/DropdownLabel.vue';
import DropdownTarget from 'src/components/DropdownTarget/DropdownTarget.vue';
import FakeSelect from 'src/components/FakeSelect/FakeSelect.vue';
import SuggestionItems from 'src/components/suggestions/SuggestionItems/SuggestionItems.vue';

export default {
  name: 'DropdownSelect',
  components: {
    FakeSelect,
    DropdownTarget,
    DropdownLabel,
    SuggestionItems,
    BasicDropdown,
  },
  inject: ['mediaQueries'],
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    value: {
      type: [Object, String, Number],
      default: () => null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    fullWidth: Boolean,
    noAutoSize: Boolean,
    name: String,
    disabled: Boolean,
  },
  emits: ['input', 'update:modelValue'],
  data() {
    return {
      activeSuggestionIndex: null,
      labelWidth: 'auto',
      selectedOption: this.getOption(this.value),
    };
  },
  watch: {
    value(newValue) {
      this.selectedOption = this.getOption(newValue);
    },
  },
  methods: {
    onUp(event) {
      event.preventDefault();
      this.moveSelection(-1);
    },
    onDown(event) {
      event.preventDefault();
      this.moveSelection(1);
    },
    moveSelection(step) {
      if (!this.options.length) return;

      const { activeSuggestionIndex } = this;
      const nextCircularIndex = rotateIndex(activeSuggestionIndex, step);
      this.activeSuggestionIndex = resolveCircularIndex(
        nextCircularIndex,
        this.options.length,
      );
    },
    onEnter() {
      const suggestion = this.activeSuggestion;
      if (!suggestion) return;

      this.selectOption(suggestion);
      this.activeSuggestionIndex = null;
    },
    onMouseover() {
      this.activeSuggestionIndex = null;
    },
    selectOption(option) {
      if (option.value === this.modelValue) {
        this.$refs.dropdownTarget.hide();
        return;
      }
      this.selectedOption = option;
      this.modelValue = option.value;
      this.$emit('update:modelValue', option.value);
      this.$refs.dropdownTarget.hide();
    },
    getOption(value) {
      return this.options.find((option) => option.value === value) || null;
    },
    setWidth(width) {
      this.labelWidth = `${Math.trunc(width)}px`;
    },
  },
  computed: {
    modelValue: createPropModel(),
    activeSuggestion() {
      const index = this.activeSuggestionIndex;
      if (index === null) return null;

      return this.options && this.options[index];
    },
    optionTitles() {
      return this.options.map((option) => option.title);
    },
  },
};
</script>
